import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "../../lib/vvm";
import {
  type BoxContentsData,
  type BoxListItem,
  type BoxProductItem,
  type IWarehouseApi,
  type ProductJournalEntry,
  WAREHOUSE_API_TOKEN,
} from "../../api/WarehouseApi";
import { Box as BoxModel } from "../../stores/Box";
import { Measures } from "../../types/entities";
import { formatTypeTag, normalizeTypeTag } from "../../utils/typeTags";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
  Screens,
} from "../../stores/navigation";
import {
  openProductsListByMeasure,
  openProductsListByTypeTag,
} from "../shared/use-cases/products-list-navigation";
import {
  type BoxHistoryItemView,
  buildBoxHistoryItems,
} from "./viewmodel/history";
import {
  type DestroyConfirmTarget,
  createChildBoxDestroyTarget,
  createCurrentBoxDestroyTarget,
  createProductDestroyTarget,
  getDestroyConfirmDescription,
  getDestroyConfirmTitle,
} from "./viewmodel/destroy-confirm";
import {
  createBoxModelFromListItem,
  createProductModelFromBoxItem,
} from "./viewmodel/models";
import {
  loadBoxContentsOperation,
  loadBoxHistoryOperation,
} from "./viewmodel/loading";
import { createBoxPageOperationBindings } from "./viewmodel/operation-bindings";
import { performDestroyByConfirmOperation } from "./viewmodel/destroy-confirm-operation";
import {
  openBoxByIdWithActionError,
  openBoxesListScreen,
  resolveParentBoxId,
  resolveReferenceId,
} from "./viewmodel/reference-navigation";
import {
  executeDeleteChildBox,
  executeDeleteCurrentBox,
  executeDeleteProductFromBox,
  executeDetachChildBox,
  executeOpenHistoryBox,
  executeOpenHistoryProduct,
  executeRemoveProductFromBox,
  type BoxPageOperationBindings,
} from "./viewmodel/operations-controller";

export interface BoxPageViewProps {
  box: BoxModel;
}

@injectable()
export class BoxPageViewModel extends ViewModel<unknown, BoxPageViewProps> {
  @observable.ref
  contents: BoxContentsData | null = null;

  @observable
  isLoading = false;

  @observable
  loadError: string | null = null;

  @observable.shallow
  history: ProductJournalEntry[] = [];

  @observable.ref
  historyProductNamesById: Record<string, string> = {};

  @observable
  isHistoryLoading = false;

  @observable
  historyError: string | null = null;

  @observable
  actionError: string | null = null;

  @observable
  busyProductId: string | null = null;

  @observable
  busyBoxId: string | null = null;

  @observable
  isDeletingCurrentBox = false;

  @observable.ref
  destroyConfirmTarget: DestroyConfirmTarget | null = null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get box(): BoxModel {
    return this.viewProps.box;
  }

  @computed
  get childBoxes(): BoxListItem[] {
    return this.contents?.contents.boxes || [];
  }

  @computed
  get childProducts(): BoxProductItem[] {
    return this.contents?.contents.products || [];
  }

  @computed
  get isDestroyConfirmOpen(): boolean {
    return Boolean(this.destroyConfirmTarget);
  }

  @computed
  get isDestroyConfirmProcessing(): boolean {
    return (
      this.isDeletingCurrentBox || !!this.busyBoxId || !!this.busyProductId
    );
  }

  @computed
  get destroyConfirmTitle(): string {
    return getDestroyConfirmTitle(this.destroyConfirmTarget);
  }

  @computed
  get destroyConfirmDescription(): string {
    return getDestroyConfirmDescription({
      target: this.destroyConfirmTarget,
      childBoxesCount: this.childBoxes.length,
      childProductsCount: this.childProducts.length,
    });
  }

  @computed
  get historyItems(): BoxHistoryItemView[] {
    return buildBoxHistoryItems({
      box: this.box,
      entries: this.history,
      childBoxes: this.childBoxes,
      childProducts: this.childProducts,
      historyProductNamesById: this.historyProductNamesById,
    });
  }

  protected onViewMounted(): void {
    void this.loadAll();
  }

  @action.bound
  async loadAll(): Promise<void> {
    await Promise.all([this.loadContents(), this.loadHistory()]);
  }

  @action.bound
  async loadContents(): Promise<void> {
    await loadBoxContentsOperation({
      state: this,
      api: this.api,
      boxId: this.box.id,
    });
  }

  @action.bound
  async loadHistory(): Promise<void> {
    await loadBoxHistoryOperation({
      state: this,
      api: this.api,
      boxId: this.box.id,
      childProducts: this.childProducts,
    });
  }

  @action.bound
  async removeProductFromBox(item: BoxProductItem): Promise<void> {
    await executeRemoveProductFromBox({
      bindings: this.operationBindings,
      item,
    });
  }

  @action.bound
  async deleteProductFromBox(
    item: BoxProductItem,
    deleteReason?: string | null,
  ): Promise<void> {
    await executeDeleteProductFromBox({
      bindings: this.operationBindings,
      item,
      deleteReason,
    });
  }

  @action.bound
  async detachChildBox(item: BoxListItem): Promise<void> {
    await executeDetachChildBox({
      bindings: this.operationBindings,
      item,
    });
  }

  @action.bound
  async deleteChildBox(
    item: BoxListItem,
    deleteReason?: string | null,
  ): Promise<void> {
    await executeDeleteChildBox({
      bindings: this.operationBindings,
      item,
      deleteReason,
    });
  }

  @action.bound
  async deleteCurrentBox(deleteReason?: string | null): Promise<void> {
    await executeDeleteCurrentBox({
      bindings: this.operationBindings,
      deleteReason,
    });
  }

  @action.bound
  openDeleteCurrentBoxConfirm(): void {
    this.openDestroyConfirmTarget(createCurrentBoxDestroyTarget(this.box.name));
  }

  @action.bound
  openDeleteChildBoxConfirm(item: BoxListItem): void {
    this.openDestroyConfirmTarget(createChildBoxDestroyTarget(item));
  }

  @action.bound
  openDeleteProductFromBoxConfirm(item: BoxProductItem): void {
    this.openDestroyConfirmTarget(createProductDestroyTarget(item));
  }

  @action.bound
  closeDestroyConfirm(): void {
    if (this.isDestroyConfirmProcessing) {
      return;
    }

    this.destroyConfirmTarget = null;
  }

  @action.bound
  confirmDestroy(deleteReason?: string | null): void {
    if (!this.destroyConfirmTarget || this.isDestroyConfirmProcessing) {
      return;
    }

    void performDestroyByConfirmOperation({
      target: this.destroyConfirmTarget,
      childBoxes: this.childBoxes,
      childProducts: this.childProducts,
      deleteCurrentBox: this.deleteCurrentBox,
      deleteChildBox: this.deleteChildBox,
      deleteProductFromBox: this.deleteProductFromBox,
      setActionError: (message) => {
        this.setActionError(message);
      },
      clearTarget: () => {
        runInAction(() => {
          this.destroyConfirmTarget = null;
        });
      },
      deleteReason,
    });
  }

  @action.bound
  openAddProduct(): void {
    this.navigationStore.setScreen(Screens.AddProduct, {
      parentId: this.box.id,
    });
  }

  @action.bound
  openEditBox(): void {
    this.navigationStore.setScreen(Screens.EditBox, this.box);
  }

  @action.bound
  openBoxTypeTagFilter(tag: string): void {
    const normalizedTag = normalizeTypeTag(tag || "");
    if (!normalizedTag) {
      return;
    }

    this.navigationStore.setScreen(Screens.BoxesList, {
      title: `Коробки по тегу ${formatTypeTag(normalizedTag)}`,
      typeTag: normalizedTag,
    });
  }

  @action.bound
  openProductTypeTagFilter(tag: string): void {
    openProductsListByTypeTag(this.navigationStore, tag);
  }

  @action.bound
  openProductMeasureFilter(measure: Measures): void {
    openProductsListByMeasure(this.navigationStore, measure);
  }

  @action.bound
  openParentBoxReference(): void {
    const parentId = resolveParentBoxId(this.box.parentId);
    if (parentId) {
      void this.openBoxById(parentId);
      return;
    }
    openBoxesListScreen(this.navigationStore);
  }

  @action.bound
  openChildBox(item: BoxListItem): void {
    this.navigationStore.setScreen(
      Screens.Box,
      createBoxModelFromListItem(item),
    );
  }

  @action.bound
  openProduct(item: BoxProductItem): void {
    this.navigationStore.setScreen(
      Screens.Product,
      createProductModelFromBoxItem(item),
    );
  }

  @action.bound
  async openHistoryProduct(productId: string): Promise<void> {
    await executeOpenHistoryProduct({
      bindings: this.operationBindings,
      productId,
    });
  }

  @action.bound
  async openHistoryBox(boxId: string): Promise<void> {
    await executeOpenHistoryBox({
      bindings: this.operationBindings,
      boxId,
    });
  }

  @action.bound
  openReferenceId(id: string): void {
    const resolved = resolveReferenceId(id);
    if (!resolved.id) {
      return;
    }

    if (resolved.kind === "product") {
      void this.openHistoryProduct(resolved.id);
      return;
    }

    if (resolved.kind === "box") {
      void this.openHistoryBox(resolved.id);
    }
  }

  private get isNestedMutationLocked(): boolean {
    return (
      this.isDeletingCurrentBox || Boolean(this.busyProductId || this.busyBoxId)
    );
  }

  private get canDeleteCurrentBoxNow(): boolean {
    return (
      !this.isDeletingCurrentBox &&
      !this.busyProductId &&
      !this.busyBoxId &&
      !this.isLoading &&
      !this.isHistoryLoading
    );
  }

  private get operationBindings(): BoxPageOperationBindings {
    return createBoxPageOperationBindings({
      api: this.api,
      navigationStore: this.navigationStore,
      box: this.box,
      isNestedMutationLocked: () => this.isNestedMutationLocked,
      canDeleteCurrentBoxNow: () => this.canDeleteCurrentBoxNow,
      setBusyProductId: (id) => {
        runInAction(() => {
          this.busyProductId = id;
        });
      },
      setBusyBoxId: (id) => {
        runInAction(() => {
          this.busyBoxId = id;
        });
      },
      setIsDeletingCurrentBox: (value) => {
        runInAction(() => {
          this.isDeletingCurrentBox = value;
        });
      },
      clearActionError: () => {
        runInAction(() => {
          this.actionError = null;
        });
      },
      setActionError: (message) => {
        this.setActionError(message);
      },
      reload: async () => this.loadAll(),
    });
  }

  private setActionError(message: string | null): void {
    runInAction(() => {
      this.actionError = message;
    });
  }

  private openDestroyConfirmTarget(target: DestroyConfirmTarget): void {
    if (this.isNestedMutationLocked) {
      return;
    }

    this.actionError = null;
    this.destroyConfirmTarget = target;
  }

  private async openBoxById(boxId: string): Promise<void> {
    await openBoxByIdWithActionError({
      api: this.api,
      navigationStore: this.navigationStore,
      boxId,
      setActionError: (message) => {
        this.setActionError(message);
      },
    });
  }
}
